import { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { Address } from '@sg/graphql-schema';

import { checkIfAddressIsDuplicateByName } from '../../utils';

/**
 * Returns a memoed result of address duplication by name.
 */
export function useAddressDuplicateByName(props: AddressDuplicateByNameProps) {
  const { addressId, addressName, isCustomNameTouched, addresses } = props;

  const { formatMessage } = useIntl();

  return useMemo(() => {
    const hasHomeAddress = checkIfAddressIsDuplicateByName({
      addressId,
      addressName: 'home',
      addresses,
    });

    const hasWorkAddress = checkIfAddressIsDuplicateByName({
      addressId,
      addressName: 'work',
      addresses,
    });

    const isCustomNameMissing = isCustomNameTouched && !addressName;
    const isCustomNameDuplicate =
      isCustomNameTouched &&
      checkIfAddressIsDuplicateByName({
        addressId,
        addressName,
        addresses,
      });

    const isCustomNameInvalid = isCustomNameDuplicate || isCustomNameMissing;

    const customNameNotice = getCustomNameNotice(formatMessage, {
      isCustomNameDuplicate,
      isCustomNameMissing,
    });

    return {
      hasHomeAddress,
      hasWorkAddress,
      isCustomNameInvalid,
      customNameNotice,
    };
  }, [addressId, addressName, addresses, isCustomNameTouched, formatMessage]);
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const getCustomNameNotice = (
  formatMessage: ReturnType<typeof useIntl>['formatMessage'],
  props: CustomNameNoticeProps,
) => {
  if (props.isCustomNameDuplicate) {
    return formatMessage(messages.nameDuplicate);
  }

  if (props.isCustomNameMissing) {
    return formatMessage(messages.nameMissing);
  }
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  nameDuplicate: {
    defaultMessage: 'You already have an address with this name',
    description: 'Complete address form | Duplicate name',
  },
  nameMissing: {
    defaultMessage: 'Please inform a custom name for this address',
    description: 'Complete address form | Missing name',
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type AddressDuplicateByNameProps = Readonly<{
  addressId?: string;
  addressName?: string;
  isCustomNameTouched: boolean;
  addresses: ReadonlyArray<Partial<Address>>;
}>;

type CustomNameNoticeProps = Readonly<{
  isCustomNameDuplicate?: boolean;
  isCustomNameMissing?: boolean;
}>;
